/* ---------------------------------------------------------------------------
   The 3D view: the structure on its ground, in the register.

   A terrain mesh from the elevation tiles (./terrain.ts), draped with the same
   raster tiles the 2D map draws, with the deck standing on it at the register's own
   length, width and axis. Orbit it, and you see how a crossing sits between its
   banks — which is the one thing a flat map cannot show.

   THREE.JS IS LOADED ON DEMAND. It is already a dependency, for the vendored
   modeller, but the modeller lives in its own 1.6 MB chunk on its own page and the
   register's bundle is 90 kB. Importing three at the top of this module would put
   ~600 kB of it into the register for a view most sessions never open, so the
   import is inside `mount` and Vite splits it out. Nothing above this module knows
   that; `syncMapScene` is synchronous to call and resolves it in the background.

   IT OWNS ITS CANVAS, outside the render root. A WebGL canvas cannot be rebuilt by
   a reconciler — the context, the camera position and every uploaded texture would
   go with it — so the element is created once here and moved into whatever slot the
   current render put on screen, which is the same arrangement the modeller's
   viewport uses (src/modeler/viewport.ts) for the same reason.

   THE VERTICAL IS RELATIVE, and this is the honest part. The elevation dataset and
   the register's levels disagree by between −68 m and +82 m across these five
   structures — SRTM smooths a gorge into its shoulders, and a Nepali drawing's
   levels are often on a project benchmark rather than a national datum. So the
   ground under the pin is zero, and the deck sits above it by the freeboard the
   record states (`deckRL − hfl`). Every height on screen is therefore a real
   recorded clearance over an approximate surface, and no absolute level is claimed
   anywhere. See the note at the top of ./terrain.ts.
   --------------------------------------------------------------------------- */

import type { BridgeView } from '../data/types.ts';
import type { MapBase } from '../data/types.ts';
import { loadGeometryBytes, parseGlb } from '../core/geometryCache.ts';
import { sourceOf, tileUrl } from './nepalMap.ts';
import {
  drapeTilesFor,
  loadTerrain,
  terrainGeometry,
  toLocal,
  type Patch,
} from './terrain.ts';

/**
 * Deck length assumed when the register has none, for FRAMING ONLY.
 *
 * Used to size the terrain patch and to place the camera, never to draw
 * anything. 60 m is about the middle of this register's range and its only job
 * is to open the viewer at a sensible zoom over a crossing whose deck nobody has
 * measured; nothing on screen claims to be 60 m long, because the placeholder
 * deck is omitted entirely in that case rather than drawn at this size.
 */
const NOMINAL_SPAN_M = 60;

/** Side of the ground patch, in metres, for a structure of this length. */
function spanFor(length: number): number {
  // Enough valley to read the crossing — about eight deck lengths — inside bounds
  // that keep the DEM request to a couple of dozen tiles either way.
  return Math.min(2400, Math.max(700, length * 8));
}

/**
 * Grid resolution of the terrain mesh.
 *
 * 128² is 16k vertices and 32k triangles: nothing for a GPU, and fine enough that
 * a 700 m patch has a sample every 5.5 m — finer than the DEM itself, which is the
 * point at which more would be interpolation dressed up as detail.
 */
const SAMPLES = 128;

/** Zoom for the draped imagery, bounded by what the sources actually serve. */
function drapeZoom(base: MapBase, span: number): number {
  // One texture pixel per ~0.6 m of ground at the tightest, which is about as much
  // as the imagery holds; then clamped to the source's own ceiling.
  const wanted = Math.round(Math.log2((40_075_016.686 * 0.7) / span));
  return Math.max(12, Math.min(sourceOf(base).maxZoom, wanted));
}

/** What is currently on screen, so a repaint can decide whether to rebuild. */
interface Showing {
  bridgeId: string;
  base: MapBase;
  /** Which model's geometry is drawn, or undefined for the recorded-dimensions box. */
  geometry: string | undefined;
  /** Bumped by the caller when the structure's own geometry changed under it. */
  revision: number;
}

let host: HTMLDivElement | null = null;
let showing: Showing | null = null;
let disposer: (() => void) | null = null;
/** Guards against two builds racing when a repaint lands mid-load. */
let building = 0;

/** Reported upward so the card can say what the view is showing. */
export interface SceneStatus {
  state: 'idle' | 'loading' | 'ready' | 'failed';
  message: string;
  /** The DEM's absolute elevation under the structure, once known. */
  groundElevation: number | null;
  relief: number | null;
  /** Whether the structure on screen is the modeller's geometry or a box. */
  source: 'model' | 'box' | null;
  /** How tall what is drawn came out, metres above the ground under the pin. */
  deckHeight: number | null;
}

let status: SceneStatus = {
  state: 'idle',
  message: '',
  groundElevation: null,
  relief: null,
  source: null,
  deckHeight: null,
};
let onStatus: (s: SceneStatus) => void = () => {};

/** Called by the card once, so it can re-render when the scene has something to say. */
export function wireSceneStatus(fn: (s: SceneStatus) => void): void {
  onStatus = fn;
}

function setStatus(next: Partial<SceneStatus>): void {
  status = { ...status, ...next };
  onStatus(status);
}

export function sceneStatus(): SceneStatus {
  return status;
}

/* --------------------------- textures --------------------------- */

/**
 * The draped map, as one canvas.
 *
 * Tiles are drawn into a single atlas rather than uploaded one per mesh: the
 * terrain is one mesh with one material, and a dozen textures on it would mean a
 * dozen draw calls and a seam at every join. `crossOrigin` is what makes them
 * usable at all — WebGL refuses to texture from an image the host has not
 * CORS-approved, and both tile sources send `Access-Control-Allow-Origin: *`.
 */
async function drapeTexture(patch: Patch, base: MapBase): Promise<HTMLCanvasElement> {
  const zoom = drapeZoom(base, patch.span);
  const tiles = drapeTilesFor(patch, zoom);

  // One canvas sized to the tiles, so nothing is resampled on the way in.
  const px = Math.max(512, Math.min(4096, Math.round(1 / (tiles[0]?.size ?? 0.25)) * 256));
  const canvas = document.createElement('canvas');
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // A neutral ground under any tile that fails, so a gap reads as missing imagery
  // rather than as a hole in the world.
  ctx.fillStyle = '#d9d6cf';
  ctx.fillRect(0, 0, px, px);

  await Promise.all(
    tiles.map(async (t) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = tileUrl(base, t.z, t.x, t.y);
      try {
        await image.decode();
      } catch {
        return; // one missing tile, not a failed view
      }
      ctx.drawImage(image, t.u * px, t.v * px, t.size * px, t.size * px);
    }),
  );

  return canvas;
}

/**
 * The modeller's exported geometry, if this model has any.
 *
 * `GLTFLoader` comes out of the three package already in this project rather than
 * from a CDN, and it is imported here rather than at the top so it lands in the same
 * on-demand chunk as three itself. A failure resolves to null rather than throwing:
 * geometry is an enhancement over the box, and losing it should cost detail, not
 * the whole view.
 */
async function loadGeometry(
  THREE: typeof import('three'),
  b: BridgeView & { hasGeometryFor?: string; geometryVersion?: string | null },
): Promise<import('three').Object3D | null> {
  if (!b.hasGeometryFor) return null;
  const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
  try {
    /* Out of src/core/geometryCache.ts, so a structure drawn here before draws again
       without the download — this view already makes the user wait on a DEM and a
       dozen imagery tiles, and the model was the largest of the three.

       NO `onUpdated` HANDLER. The structure's placement here is solved once, from
       its own bounds, against the terrain patch under it (see `build` below), so
       swapping the model live would mean redoing that arithmetic against a camera
       somebody has already moved. The cache stores the newer bytes either way and
       this view opens on them next time — which for a view keyed on a rebuild
       (`revision`, `hasGeometryFor`) is soon enough. */
    const { bytes } = await loadGeometryBytes(b.id, b.hasGeometryFor, undefined, b.geometryVersion);
    const gltf = await parseGlb(new GLTFLoader(), bytes);
    // Everything the exporter emitted, as one object to place. Shadows are off:
    // there is no shadow map in this scene, and the terrain's own relief is what
    // reads the ground.
    const root = gltf.scene as import('three').Object3D;
    root.traverse((node) => {
      const mesh = node as import('three').Mesh;
      if (mesh.isMesh) mesh.frustumCulled = true;
    });
    void THREE;
    return root;
  } catch {
    return null;
  }
}

/* --------------------------- the scene --------------------------- */

/**
 * Build everything and start rendering.
 *
 * Returns a disposer, and it has to: a WebGL context, its geometries and its
 * textures are not garbage — Chrome caps a tab at around sixteen live contexts,
 * and the modeller holds one or two of its own. Every path out of this view goes
 * through the returned function.
 */
async function build(
  slot: HTMLElement,
  b: BridgeView & { hasGeometryFor?: string; geometryVersion?: string | null },
  base: MapBase,
): Promise<() => void> {
  const THREE = await import('three');
  const { default: CameraControls } = await import('camera-controls');
  CameraControls.install({ THREE: THREE as unknown as Parameters<typeof CameraControls.install>[0]['THREE'] });

  /* THE PATCH IS ABOUT THE GROUND, NOT THE BRIDGE, which is why an unrecorded
     length falls back here rather than refusing the scene. Somebody looking at a
     crossing whose deck nobody has measured still needs to see the valley it
     crosses, and `NOMINAL_SPAN_M` is a viewport extent — it sizes the terrain
     tile fetched, and nothing drawn in it claims to be that long. The deck box
     below is what would have made a claim, and it is omitted instead. */
  const span = spanFor(b.length ?? NOMINAL_SPAN_M);
  const patch: Patch = { lat: b.lat!, lng: b.lng!, span, samples: SAMPLES };
  /* Every camera framing below is a multiple of the structure's length. Same
     fallback, same reason: a camera distance is not a measurement. */
  const framing = b.length ?? NOMINAL_SPAN_M;

  setStatus({ state: 'loading', message: 'Reading the ground…' });
  const [terrain, drape] = await Promise.all([loadTerrain(patch), drapeTexture(patch, base)]);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  renderer.setSize(slot.clientWidth || 900, slot.clientHeight || 420, false);
  renderer.domElement.className = 'nmap3d__canvas';
  slot.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(
    45,
    (slot.clientWidth || 900) / (slot.clientHeight || 420),
    1,
    span * 6,
  );

  // Ground.
  const { positions, uvs, indices } = terrainGeometry(patch, terrain.heights);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.computeVertexNormals();

  const texture = new THREE.CanvasTexture(drape);
  texture.colorSpace = THREE.SRGBColorSpace;
  const ground = new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({ map: texture, roughness: 0.95, metalness: 0 }),
  );
  scene.add(ground);

  /* The structure. Either the modeller's own geometry, if it has been exported to
     the bucket, or a box at the register's recorded length × width.

     ROTATION IS THE SAME FOR BOTH, and it is not a guess: the modeller's local
     frame is +X east-ish, +Z north-ish, Y up, documented in its own KmlExporter
     where a heading of 0 means +X points due east. A three.js rotation of `a` about
     Y takes +X to (cos a east, sin a north), so a deck axis β from north wants
     a = 90° − β. A box's own length runs along X too, so one line serves both.

     Height is where they differ, and each is the honest choice for what it is. A
     box has no piers, so it stands at the recorded freeboard above the ground. Real
     geometry HAS piers and knows how tall they are, so it is set down with its
     lowest point on the ground and its deck ends up wherever the model says —
     which the footer then compares with the recorded freeboard, because the two
     disagreeing is worth knowing. */
  const at = toLocal(patch, b.lat!, b.lng!);
  const spin = ((90 - (b.deckBearing ?? 0)) * Math.PI) / 180;
  const freeboard = b.freeboard ?? 8;

  /** Whatever ended up standing on the ground, for the disposer to walk. */
  let structure: import('three').Object3D | null = null;
  let deckHeight = freeboard;
  let geometrySource: 'model' | 'box' = 'box';
  const model = b.hasGeometryFor ? await loadGeometry(THREE, b) : null;

  if (model) {
    geometrySource = 'model';
    model.rotation.y = spin;
    // Measured AFTER the rotation, so the box is the footprint as placed.
    const bounds = new THREE.Box3().setFromObject(model);
    const size = new THREE.Vector3();
    const mid = new THREE.Vector3();
    bounds.getSize(size);
    bounds.getCenter(mid);
    model.position.set(at.x - mid.x, -bounds.min.y, at.z - mid.z);
    deckHeight = size.y;
    structure = model;
    scene.add(model);
  } else if (b.length != null && b.deckWidth != null) {
    const thickness = Math.max(1.2, Math.min(4, b.length / 40));
    const deck = new THREE.Mesh(
      new THREE.BoxGeometry(b.length, thickness, b.deckWidth),
      new THREE.MeshStandardMaterial({ color: 0x2f6fd0, roughness: 0.6, metalness: 0.1 }),
    );
    deck.rotation.y = spin;
    deck.position.set(at.x, freeboard, at.z);
    structure = deck;
    scene.add(deck);
  }
  /* NO BOX WHERE THERE IS NOTHING TO SIZE IT FROM. The placeholder deck exists
     to show a structure at its RECORDED length and width over its own ground;
     with either unrecorded there is no size to draw and a box at a nominal one
     would be a slab on a hillside asserting a span nobody measured. The terrain,
     the pin and the record's own "not recorded" are the honest answer. */

  // Light enough to read relief: one sun, low, plus sky fill.
  const sun = new THREE.DirectionalLight(0xffffff, 2.2);
  sun.position.set(-span * 0.4, span * 0.5, span * 0.3);
  scene.add(sun);
  scene.add(new THREE.HemisphereLight(0xdfe8f5, 0x6b6255, 1.1));

  // Camera: a three-quarter view from the south-west, framed on the structure.
  const controls = new CameraControls(camera, renderer.domElement);
  controls.minDistance = Math.max(40, framing * 0.4);
  controls.maxDistance = span * 2;
  // Do not let the camera go under the ground; nothing is modelled down there.
  controls.maxPolarAngle = Math.PI / 2 - 0.05;
  void controls.setLookAt(
    at.x - framing * 1.1,
    freeboard + Math.max(120, framing * 0.9),
    at.z + framing * 1.4,
    at.x,
    freeboard,
    at.z,
    false,
  );

  let raf = 0;
  const clock = new THREE.Clock();
  const tick = (): void => {
    raf = requestAnimationFrame(tick);
    controls.update(clock.getDelta());
    renderer.render(scene, camera);
  };
  tick();

  const onResize = (): void => {
    const w = slot.clientWidth || 900;
    const h = slot.clientHeight || 420;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
  };
  const observer =
    typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onResize);
  observer?.observe(slot);

  setStatus({
    state: 'ready',
    message:
      terrain.tiles === 0
        ? 'No elevation data here — the ground is drawn flat.'
        : `Ground from ${terrain.tiles} elevation tile${terrain.tiles === 1 ? '' : 's'} at z${terrain.zoom}.`,
    groundElevation: terrain.centreElevation,
    relief: terrain.max - terrain.min,
    source: geometrySource,
    deckHeight,
  });

  return () => {
    cancelAnimationFrame(raf);
    observer?.disconnect();
    controls.dispose();
    geometry.dispose();
    texture.dispose();
    (ground.material as { dispose(): void }).dispose();
    /* Walked rather than named: the structure is either one box or a whole glTF
       scene, and either way every geometry and material in it holds GPU memory
       that a dropped reference does not free. */
    structure?.traverse((node) => {
      const mesh = node as import('three').Mesh;
      mesh.geometry?.dispose();
      const material = mesh.material as { dispose?(): void } | { dispose?(): void }[] | undefined;
      if (Array.isArray(material)) material.forEach((m) => m.dispose?.());
      else material?.dispose?.();
    });
    renderer.dispose();
    renderer.domElement.remove();
  };
}

/**
 * Reconcile the 3D view with state, after the render — as with the 2D map's
 * measurement and the modeller's frames, because the slot only exists once the
 * render is done.
 *
 * Rebuilds only when the structure, the base map or the record's geometry has
 * actually changed. A repaint for anything else — a pan on the 2D map behind it, a
 * theme toggle — leaves the context, the camera and every texture where they are.
 */
export function syncMapScene(
  wanted: {
    bridge: BridgeView;
    /** Model id whose exported geometry to draw, or undefined for the box. */
    hasGeometryFor?: string;
    /**
     * That geometry's fingerprint from the model index, where the caller has it.
     *
     * Not part of `showing` below and deliberately not: this view is rebuilt on a
     * change of structure, base map or register revision, and a new export does
     * not need to tear down a terrain patch and a camera. What it does is let the
     * cache skip its freshness request when what is held is already current.
     */
    geometryVersion?: string | null;
    base: MapBase;
    revision: number;
  } | null,
): void {
  const slot = document.querySelector<HTMLDivElement>('.nmap3d__slot');

  if (!wanted || !slot || wanted.bridge.lat == null || wanted.bridge.lng == null) {
    if (disposer) {
      disposer();
      disposer = null;
    }
    host = null;
    showing = null;
    if (status.state !== 'idle') {
      setStatus({
        state: 'idle',
        message: '',
        groundElevation: null,
        relief: null,
        source: null,
        deckHeight: null,
      });
    }
    return;
  }

  const same =
    showing !== null &&
    host === slot &&
    showing.bridgeId === wanted.bridge.id &&
    showing.base === wanted.base &&
    showing.geometry === wanted.hasGeometryFor &&
    showing.revision === wanted.revision;
  if (same) return;

  if (disposer) {
    disposer();
    disposer = null;
  }
  host = slot;
  showing = {
    bridgeId: wanted.bridge.id,
    base: wanted.base,
    geometry: wanted.hasGeometryFor,
    revision: wanted.revision,
  };

  const token = ++building;
  void build(
    slot,
    wanted.hasGeometryFor === undefined
      ? wanted.bridge
      : {
          ...wanted.bridge,
          hasGeometryFor: wanted.hasGeometryFor,
          geometryVersion: wanted.geometryVersion ?? null,
        },
    wanted.base,
  )
    .then((dispose) => {
      // A newer build started while this one was loading: throw this one away
      // rather than leaving two contexts alive.
      if (token !== building) {
        dispose();
        return;
      }
      disposer = dispose;
    })
    .catch((err: unknown) => {
      if (token !== building) return;
      /* `showing` is deliberately LEFT SET — the same reason as in ./flyMap.ts.
         This status change repaints, a repaint calls this function again, and a
         cleared `showing` would read as "nothing is showing here" and start the
         build afresh: a failing elevation fetch would then be retried for as long
         as the view was open. Reported once; a change of structure, base map or
         record moves `showing` and tries again on its own. */
      setStatus({
        state: 'failed',
        message:
          err instanceof Error
            ? `The 3D view could not be built: ${err.message}`
            : 'The 3D view could not be built.',
        groundElevation: null,
        relief: null,
        source: null,
        deckHeight: null,
      });
    });
}
